import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/homeData';
import { categoryVideos } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { slug: currentSlug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(currentSlug || 'gaoxiao');

  useEffect(() => {
    window.scrollTo(0, 0);
    if (currentSlug) {
      setActiveCategory(currentSlug);
    }
  }, [currentSlug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const videos = categoryVideos[activeCategory] || categoryVideos.gaoxiao;

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700">分类浏览</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 font-medium">{currentCategory.name}</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
              <strong className="text-primary-500">{currentCategory.name}</strong> 视频分类
            </h1>
            <p className="text-sm text-foreground-500">
              共 {(currentCategory.count / 10000).toFixed(1)} 万+ 个精彩视频等你发现
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10 pb-6 border-b border-background-200/70 overflow-x-auto">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {videos.map((video) => (
              <Link
                key={video.slug}
                to={`/article/${video.slug}`}
                className="group rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-48 md:h-52 overflow-hidden">
                  <img
                    src={video.image}
                    alt={video.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-3 right-3 px-2 py-1 rounded-md bg-background-950/75 text-background-50 text-xs font-medium">
                    {video.duration}
                  </div>
                  <div className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/10 transition-colors flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-play-fill text-background-50 text-xl"></i>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm md:text-base font-semibold font-heading text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-1 mb-3">{video.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i> {video.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {video.views}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 快猫视频`,
            description: `快猫视频${currentCategory.name}分类，共${(currentCategory.count / 10000).toFixed(1)}万+精彩视频。`,
            url: `https://www.kuaimaovideo.com/category/${activeCategory}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '快猫视频',
              url: 'https://www.kuaimaovideo.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kuaimaovideo.com/' },
                { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://www.kuaimaovideo.com/category/gaoxiao' },
                { '@type': 'ListItem', position: 3, name: currentCategory.name },
              ],
            },
          }),
        }}
      />
    </>
  );
}